import { handleAxiosError, notEqual } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import {
  Course,
  MakeAvailableCoursesToAccountFormValues,
  MaterialType,
} from '@/client/courses';
import { FilterNamesEnum } from '@/client/helpers';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import { AccountAvailabilityModal } from '@/components/courses/modals/AccountAvailabilityModal';
import {
  DataTable,
  DataTableActions,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterType,
  FilterTypeEnum,
} from '@/components/tables/crud';
import {
  useAccount,
  useCourses,
  useMakeAccountsUnavailable,
  useMakeCoursesAvailableToAccount,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import DatatableThumbnail from '@/ui/datatable-thumbnail/DatatableThumbnail';
import { FlexContainer } from '@/ui/styled-ui';
import { getCourseLanguages, materialTypeOptions } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import moment from 'moment';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { FormEvent, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AccountHeader } from './AccountHeader';

const pathItems = (
  account: Account,
  currentAccount: Account,
  t: (key: string, placeholder?: { name: string }) => string,
): MenuItem[] => {
  const items: MenuItem[] = [
    {
      label: currentAccount?.name,
      url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  if (currentAccount?.isSystem && account) {
    items.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  items.push({
    label: t('materials'),
    url: RedirectPaths[RedirectPathsEnum.ACCOUNT_SECURITY_DOCUMENTS](
      account?.id,
    ),
    template: AppBreadCrumbTemplate,
  });

  return items;
};

const getFilters = (t: TranslationFunctionType): FilterType[] => [
  {
    label: t('material.type'),
    field: FilterNamesEnum.MATERIAL_TYPE,
    placeholder: t('generic.type.search'),
    type: FilterTypeEnum.SELECT,
    options: materialTypeOptions(t),
  },
];

export const AccountDocumentsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { setDialogData } = useContext(DialogContext);
  const { id } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { account } = useAccount({ accountId: id });

  const [showMakeAvailableModal, setShowMakeAvailableModal] = useState(false);

  const { skip, take, sort, apiFilters, onSort, setSkip, onPage, onFilter } =
    useTable();

  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 500),
    [],
  );

  const { isLoading, courses, refetch, isFetching } = useCourses({
    accountId: account?.isSystem ? undefined : (id as string),
    take,
    skip,
    search: multiSearchValue,
    filters: [...apiFilters, notEqual('type', MaterialType.COURSE)],
    sort: sort && sort.length > 0 ? [sort.join(',')] : [],
  });

  const saveAvailableCoursesToAccount = useMakeCoursesAvailableToAccount();
  const saveUnavailableAccounts = useMakeAccountsUnavailable();

  const menuItems = (course: Course) => {
    const menu: MenuItem[] = [
      {
        label: t('generic.accounts.unavailable'),
        icon: 'pi pi-ban',
        command: () =>
          setDialogData({
            show: true,
            type: DialogType.CONFIRMATION,
            header: t('dialog.makeUnavailable'),
            message: t('dialog.makeMaterialUnavailable.confirm'),
            onAccept: () => handleSubmitUnavailable(course),
          }),
      },
    ];

    return menu;
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('material'),
      sortable: true,
      filterable: false,
      render: (row: Course) => (
        <FlexContainer gap={12}>
          {row?.courseThumbnail?.signedUrl && (
            <DatatableThumbnail
              className={
                moment(row?.releaseDate).isAfter(moment(), 'day')
                  ? 'upcoming-course'
                  : ''
              }
              url={row?.courseThumbnail?.signedUrl}
            />
          )}
          <FlexContainer direction="column" align="flex-start">
            <span>{row?.name}</span>
            {!!row?.courseLanguages?.length && (
              <span className="secondary-text">
                <span>{t('courses.available.languages')}: </span>
                {getCourseLanguages(row?.courseLanguages)}
              </span>
            )}
          </FlexContainer>
        </FlexContainer>
      ),
    },
    {
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      style: {
        width: '80px',
        textAlign: 'center',
      },
      render: (row: Course) => (
        <DataTableActions
          disabled={menuItems(row).length < 1}
          menuItems={menuItems(row)}
        />
      ),
    },
  ];

  const toolbar = (
    <DataTableToolbar>
      <DataTableFilters
        filters={getFilters(t)}
        onFilter={onFilter}
        className="flex-initial"
      />
      <div className="p-input-icon-left flex-auto min-w-300">
        <InputText
          className="w-full"
          onInput={debouncedSetMultiSearchValue}
          placeholder={t('material.search.name')}
          autoComplete="off"
        />
        <i className="pi pi-search" />
      </div>
      <AppButton
        label={t('button.makeMaterialsAvailable')}
        severity="secondary"
        onClick={() => setShowMakeAvailableModal(true)}
      />
    </DataTableToolbar>
  );

  const onRowClick = (e: DataTableRowClickEvent) => {
    if (isLoading) return;
    navigate(
      RedirectPaths[
        e?.data?.type === MaterialType.RESOURCE
          ? RedirectPathsEnum.RESOURCES_EDIT
          : e?.data?.type === MaterialType.CUSTOM_MATERIAL
          ? RedirectPathsEnum.CUSTOM_MATERIALS_EDIT
          : RedirectPathsEnum.POSTER_EDIT
      ](e.data.id),
      { state: { from: location.pathname + location.search } },
    );
  };

  const handleSubmitAvailable = async (
    data: MakeAvailableCoursesToAccountFormValues,
  ) => {
    if (account) {
      try {
        await saveAvailableCoursesToAccount.add({
          courses: data?.courses?.map((course) => course.id),
          accountId: account?.id,
        });
        toast?.success(t('toast.success'), t('materials.available.added'));
        refetch();
        setShowMakeAvailableModal(false);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  const handleSubmitUnavailable = async (course: Course) => {
    if (account) {
      try {
        await saveUnavailableAccounts.remove({
          courseId: course?.id,
          accounts: [account?.id],
        });
        toast?.success(t('toast.success'), t('materials.unavailable.added'));
        refetch();
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  return (
    <>
      {!currentAccount || !account ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          <AppBreadCrumb model={pathItems(account, currentAccount, t)} />
          <AccountHeader selectedAccount={account} />

          {account && (
            <AccountAvailabilityModal
              visible={showMakeAvailableModal}
              onSubmit={handleSubmitAvailable}
              onHide={() => setShowMakeAvailableModal(false)}
              account={account}
              availableCoursesLength={courses?.count}
              isMaterial
            />
          )}

          <DataTable
            data={courses?.result}
            count={courses?.count as number}
            isLoading={isLoading}
            isFetching={isFetching}
            toolbar={toolbar}
            columns={columns}
            onPage={onPage}
            rows={take}
            skip={skip}
            onSort={onSort}
            sort={sort}
            onRowClick={onRowClick}
          />
        </>
      )}
    </>
  );
};
